import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import Dayjs from 'dayjs'
import React from 'react'

import {
  afterschoolDecision,
  fetchAfterschoolOrder,
  fetchAfterschoolPending,
  fetchMyCentres
} from 'api/procurement'
import constants from 'app/constants'
import { eventAlert } from 'app/helpers'

import {
  PO_ALIGN,
  PO_HEADERS,
  afterschoolSummary,
  poRows,
  poTotals,
  seedSpecFor
} from '../AfterschoolGames'
import { downloadOrderPdf, downloadXlsxSheets } from '../components/exportUtils'
import { errorText } from '../components/errorText'
import { orderCentres } from '../constants'

// HQ view of the afterschool-games exercise (user 2026-08-22): one table —
// what is pending to be ordered per centre. Until the counts get a backend,
// the numbers are the same saved-or-July-audit deficits each centre's own
// order view computes; on this machine that means the audit floors.
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`

const useStyles = makeStyles((theme) => ({
  topRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2)
  },
  spacer: { flex: 1 },
  fitTable: { tableLayout: 'fixed', width: '100%', background: '#fff' },
  headCell: {
    position: 'sticky',
    top: 0,
    zIndex: 3,
    background: '#fff',
    borderBottom: '2px solid #ddd'
  },
  muted: { color: theme.palette.text.secondary },
  num: { fontVariantNumeric: 'tabular-nums' },
  totalRow: { '& td': { fontWeight: 700, borderTop: '2px solid #ddd' } }
}))

// PO-format entries (user 2026-08-22): spec columns decorate the server
// lines from the seed by (game, item).
const orderPoEntries = (order) =>
  (order.lines || []).map((l) => {
    const s = seedSpecFor(l.game, l.item_name)
    return {
      name: l.item_name,
      ref: l.reference_id,
      spec: s.spec,
      paper: s.paper,
      sides: s.sides,
      qty: Number(l.qty || 0),
      rate: s.ppp != null ? s.ppp : l.unit_price,
      total: l.total != null ? Number(l.total) : null,
      remarks: l.remarks,
      link: s.link
    }
  })

const HqAfterschool = () => {
  const classes = useStyles()
  const [rows, setRows] = React.useState(null)
  const [sopOpen, setSopOpen] = React.useState(false)
  // Submitted partial orders awaiting a decision (capex-style queue).
  const [pending, setPending] = React.useState([])
  const [view, setView] = React.useState(null) // {row, order}
  const [reject, setReject] = React.useState(null)
  const [rejectRemarks, setRejectRemarks] = React.useState('')
  const [acting, setActing] = React.useState(false)

  const loadPending = React.useCallback(async () => {
    try {
      const res = await fetchAfterschoolPending()
      setPending(res.pending || [])
    } catch (e) {
      // endpoint not deployed yet — the queue section just stays empty
    }
  }, [])

  React.useEffect(() => {
    loadPending()
    ;(async () => {
      try {
        const res = await fetchMyCentres()
        const centres = orderCentres(res?.results || [])
        setRows(
          centres.map((c) => ({
            name: c.name,
            ...afterschoolSummary(c.uuid, c.name)
          }))
        )
      } catch (error) {
        eventAlert(errorText(error, 'Failed to load centres'), constants.ERROR, 'Afterschool')
      }
    })()
  }, [loadPending])

  const openView = async (row) => {
    try {
      const order = await fetchAfterschoolOrder(row.centre, row.month.slice(0, 7))
      setView({ row, order })
    } catch (error) {
      eventAlert(errorText(error, 'Failed to load the order'), constants.ERROR, 'Afterschool')
    }
  }

  const decide = async (row, action, remarks) => {
    setActing(true)
    try {
      await afterschoolDecision({
        centre: row.centre,
        month: row.month.slice(0, 7),
        action,
        remarks: remarks || ''
      })
      eventAlert(
        action === 'approve'
          ? `${row.centre_name} approved`
          : `${row.centre_name} sent back to the centre`,
        constants.SUCCESS,
        'Afterschool'
      )
      setReject(null)
      setRejectRemarks('')
      setView(null)
      loadPending()
    } catch (error) {
      eventAlert(errorText(error, 'Action failed'), constants.ERROR, 'Afterschool')
    } finally {
      setActing(false)
    }
  }

  const exportOrder = async (row, kind) => {
    try {
      const order =
        view && view.row === row
          ? view.order
          : await fetchAfterschoolOrder(row.centre, row.month.slice(0, 7))
      const entries = orderPoEntries(order)
      const fname = `Afterschool-order-${row.centre_name.replace(/\s+/g, '-')}-${row.month.slice(0, 7)}`
      if (kind === 'excel') {
        downloadXlsxSheets(fname, [
          {
            name: row.centre_name,
            headers: [...PO_HEADERS, 'File'],
            rows: [
              ...poRows(entries, { withFile: true }),
              [],
              [...poTotals(entries).map((v) => v || ''), '']
            ]
          }
        ])
      } else {
        downloadOrderPdf(fname, {
          title: 'Afterschool games — order',
          subtitle: `${row.centre_name} — ${Dayjs(row.month).format('MMMM YYYY')}`,
          po: row.po_number || undefined,
          meta: [
            { k: 'Submitted', v: Dayjs(row.submitted_at).format('DD MMM YYYY') },
            { k: 'Items', v: String(row.lines) }
          ],
          headers: PO_HEADERS,
          rows: poRows(entries),
          align: PO_ALIGN,
          totals: poTotals(entries)
        })
      }
    } catch (error) {
      eventAlert(errorText(error, 'Export failed'), constants.ERROR, 'Afterschool')
    }
  }

  const audited = (rows || []).filter((r) => r.audited)
  const totalAmount = audited.reduce((s, r) => s + r.total, 0)
  const totalItems = audited.reduce((s, r) => s + r.shortItems, 0)
  const anyUnpriced = audited.some((r) => r.unpriced > 0)

  return (
    <div>
      <div className={classes.topRow}>
        <Typography style={{ fontWeight: 700 }}>
          Afterschool games — pending to order, by centre
        </Typography>
        <IconButton
          size="small"
          title="How this page works"
          onClick={() => setSopOpen(true)}>
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
        <span className={classes.spacer} />
        <Typography variant="body2" className={classes.muted}>
          {audited.length} centres · {inr(totalAmount)}
          {anyUnpriced ? ' *' : ''}
        </Typography>
      </div>

      {pending.length > 0 && (
        <Box mb={3}>
          <Typography style={{ fontWeight: 700, marginBottom: 8 }}>
            Submitted orders awaiting a decision
          </Typography>
          <Paper variant="outlined">
            <Table size="small" className={classes.fitTable}>
              <TableHead>
                <TableRow>
                  <TableCell width="18%" className={classes.headCell}>Centre</TableCell>
                  <TableCell width="10%" className={classes.headCell}>Month</TableCell>
                  <TableCell width="18%" className={classes.headCell}>PO</TableCell>
                  <TableCell width="11%" className={classes.headCell}>Submitted</TableCell>
                  <TableCell width="8%" className={classes.headCell}>Items</TableCell>
                  <TableCell width="12%" className={classes.headCell}>Total</TableCell>
                  <TableCell width="23%" className={classes.headCell} />
                </TableRow>
              </TableHead>
              <TableBody>
                {pending.map((row) => (
                  <TableRow key={`${row.centre}-${row.month}`} hover>
                    <TableCell className={classes.muted}>{row.centre_name}</TableCell>
                    <TableCell>{Dayjs(row.month).format('MMM YYYY')}</TableCell>
                    <TableCell className={classes.muted}>{row.po_number || '—'}</TableCell>
                    <TableCell className={classes.muted}>
                      {row.submitted_at ? Dayjs(row.submitted_at).format('DD MMM') : '—'}
                    </TableCell>
                    <TableCell className={classes.num}>{row.lines}</TableCell>
                    <TableCell className={classes.num}>{inr(row.total)}</TableCell>
                    <TableCell>
                      <Box display="flex" style={{ gap: 6 }}>
                        <Button size="small" onClick={() => openView(row)}>
                          View
                        </Button>
                        <Button
                          size="small"
                          color="primary"
                          variant="contained"
                          disabled={acting}
                          onClick={() => decide(row, 'approve')}>
                          Approve
                        </Button>
                        <Button
                          size="small"
                          disabled={acting}
                          onClick={() => setReject(row)}>
                          Reject
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      )}

      {rows === null ? (
        <Box p={4} display="flex" justifyContent="center">
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Paper variant="outlined" style={{ maxWidth: 760 }}>
          <Table size="small" className={classes.fitTable}>
            <TableHead>
              <TableRow>
                <TableCell width="34%" className={classes.headCell}>
                  Centre
                </TableCell>
                <TableCell width="20%" className={classes.headCell}>
                  Games short
                </TableCell>
                <TableCell width="20%" className={classes.headCell}>
                  Items short
                </TableCell>
                <TableCell width="26%" className={classes.headCell}>
                  To order
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.name} hover>
                  <TableCell>{r.name}</TableCell>
                  {r.audited ? (
                    <>
                      <TableCell className={classes.num}>{r.shortGames}</TableCell>
                      <TableCell className={classes.num}>{r.shortItems}</TableCell>
                      <TableCell className={classes.num}>
                        {inr(r.total)}
                        {r.unpriced > 0 ? ' *' : ''}
                      </TableCell>
                    </>
                  ) : (
                    <TableCell colSpan={3} className={classes.muted}>
                      no July audit data — needs a fresh stock check
                    </TableCell>
                  )}
                </TableRow>
              ))}
              <TableRow className={classes.totalRow}>
                <TableCell>Total</TableCell>
                <TableCell className={classes.num}>—</TableCell>
                <TableCell className={classes.num}>{totalItems}</TableCell>
                <TableCell className={classes.num}>{inr(totalAmount)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      )}
      <Typography variant="caption" color="textSecondary">
        * some short items have no price yet — those amounts are floors.
        Numbers use each centre's July audit until it re-counts in its own
        stock check; saved counts live on the centre's device until the
        backend lands.
      </Typography>

      <Dialog open={!!view} onClose={() => setView(null)} maxWidth="md" fullWidth>
        {view && (
          <>
            <DialogTitle>
              {view.row.centre_name} — {Dayjs(view.row.month).format('MMMM YYYY')}
              {view.row.po_number ? ` · ${view.row.po_number}` : ''}
            </DialogTitle>
            <DialogContent>
              <Table size="small" className={classes.fitTable}>
                <TableHead>
                  <TableRow>
                    <TableCell width="20%">Game</TableCell>
                    <TableCell width="28%">Item</TableCell>
                    <TableCell width="12%">Reference ID</TableCell>
                    <TableCell width="7%">Qty</TableCell>
                    <TableCell width="9%">Unit</TableCell>
                    <TableCell width="9%">Total</TableCell>
                    <TableCell width="15%">Remarks</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(view.order.lines || []).map((l, i) => (
                    <TableRow key={`${l.game}-${l.item_name}-${i}`}>
                      <TableCell className={classes.muted}>{l.game}</TableCell>
                      <TableCell>{l.item_name}</TableCell>
                      <TableCell className={classes.muted}>
                        {l.reference_id || '—'}
                      </TableCell>
                      <TableCell className={classes.num}>{Number(l.qty)}</TableCell>
                      <TableCell className={classes.num}>
                        {l.unit_price != null ? inr(l.unit_price) : '—'}
                      </TableCell>
                      <TableCell className={classes.num}>{inr(l.total)}</TableCell>
                      <TableCell className={classes.muted}>
                        {l.remarks || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className={classes.totalRow}>
                    <TableCell>Total</TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell className={classes.num}>{inr(view.order.total)}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </DialogContent>
            <DialogActions>
              <Button size="small" onClick={() => exportOrder(view.row, 'excel')}>
                Excel
              </Button>
              <Button size="small" onClick={() => exportOrder(view.row, 'pdf')}>
                PDF
              </Button>
              <span style={{ flex: 1 }} />
              <Button size="small" onClick={() => setView(null)}>
                Close
              </Button>
              <Button
                size="small"
                disabled={acting}
                onClick={() => setReject(view.row)}>
                Reject
              </Button>
              <Button
                size="small"
                variant="contained"
                color="primary"
                disabled={acting}
                onClick={() => decide(view.row, 'approve')}>
                Approve
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog open={!!reject} onClose={() => setReject(null)} maxWidth="xs" fullWidth>
        {reject && (
          <>
            <DialogTitle>Send {reject.centre_name} back?</DialogTitle>
            <DialogContent>
              <Typography variant="body2" gutterBottom>
                The order reopens for the centre with your note.
              </Typography>
              <TextField
                autoFocus
                fullWidth
                multiline
                minRows={2}
                size="small"
                variant="outlined"
                placeholder="What should they change?"
                value={rejectRemarks}
                onChange={(e) => setRejectRemarks(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button size="small" onClick={() => setReject(null)}>
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                color="primary"
                disabled={acting}
                onClick={() => decide(reject, 'reject', rejectRemarks)}>
                Send back
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog open={sopOpen} onClose={() => setSopOpen(false)} maxWidth="xs">
        <DialogTitle>Afterschool games — HQ view</DialogTitle>
        <DialogContent>
          <Typography variant="body2" component="ol" style={{ paddingLeft: 18 }}>
            <li>
              Each centre's stock check compares its counts against the
              full games set; anything short becomes its order.
            </li>
            <li>This table is the ₹ pending per centre, at a glance.</li>
            <li>
              Counts start from the July audit; centres review and correct
              them in Centre Operations → Afterschool Games.
            </li>
            <li>
              The order documents (Excel/PDF) download from each centre's
              Order view.
            </li>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setSopOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default HqAfterschool
